/**
 * Detects URLs inside plain message text so they can be rendered as links.
 *
 * Handles three shapes: full URLs with a scheme (https://example.com),
 * "www." URLs, and bare domains such as "uljaapp.com". Bare domains are only
 * matched when they end in a known TLD, otherwise ordinary sentences like
 * "index.html" or "Hi.Thanks" would turn into links.
 */
import React from "react";

// Common TLDs. Deliberately excludes short ones that double as English words
// (.it, .at, .be, .is, .to, ...) because they cause false positives after a
// missing space, e.g. "Hello.It works".
const TLDS = [
  "com", "net", "org", "edu", "gov", "mil", "int",
  "io", "co", "ai", "app", "dev", "me", "info", "biz", "tv", "cc", "gg",
  "in", "uk", "ca", "au", "nz", "de", "fr", "es", "nl", "se", "dk", "fi",
  "pl", "ru", "br", "mx", "jp", "cn", "kr", "sg", "hk", "za", "ae", "ch",
  "pt", "gr", "ie", "il", "tr", "ua", "cz", "ro", "hu", "id", "my", "ph",
  "th", "vn", "pk", "bd", "lk", "np",
  "xyz", "online", "site", "tech", "store", "shop", "blog", "cloud", "space",
  "live", "life", "world", "today", "news", "media", "agency", "studio",
  "design", "digital", "email", "link", "page", "wiki", "fun", "pro", "chat",
];

// Characters allowed inside a URL once we know it is one.
const URL_BODY = "[^\\s<>\"'`{}|\\\\^\\[\\]]";

const EMAIL_PATTERN = "[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}";
const SCHEME_PATTERN = `(?:https?|ftp)://${URL_BODY}+`;
const WWW_PATTERN = `www\\.${URL_BODY}+`;
const BARE_PATTERN =
  `(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+` +
  `(?:${TLDS.join("|")})(?![A-Za-z0-9-])` +
  `(?::\\d{2,5})?(?:[/?#]${URL_BODY}*)?`;

// Emails are matched first so that the domain part of "me@example.com" is not
// picked up as a bare domain. They are kept as plain text.
const LINK_REGEX = new RegExp(
  `(${EMAIL_PATTERN})|(?:${SCHEME_PATTERN})|(?:${WWW_PATTERN})|(?:${BARE_PATTERN})`,
  "gi"
);

const TRAILING_PUNCTUATION = ".,;:!?'\"\u2019\u201d";

function countOccurrences(text, char) {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === char) count++;
  }
  return count;
}

// Trailing punctuation usually belongs to the sentence, not the URL. Closing
// brackets are only dropped when they are unbalanced, so paths such as
// "/wiki/Foo_(bar)" survive.
function trimTrailingPunctuation(url) {
  let result = url;

  while (result.length > 1) {
    const last = result[result.length - 1];

    if (TRAILING_PUNCTUATION.includes(last)) {
      result = result.slice(0, -1);
      continue;
    }
    if (last === ")" && countOccurrences(result, ")") > countOccurrences(result, "(")) {
      result = result.slice(0, -1);
      continue;
    }
    if (last === "]" && countOccurrences(result, "]") > countOccurrences(result, "[")) {
      result = result.slice(0, -1);
      continue;
    }
    break;
  }

  return result;
}

function toHref(url) {
  return /^[A-Za-z][A-Za-z0-9+.-]*:\/\//.test(url) ? url : `https://${url}`;
}

// A bare domain written in sentence case is almost always a missing space
// after a full stop ("Ok.In that case") rather than a real domain, so its TLD
// has to be consistently cased.
function isPlausibleBareDomain(url) {
  if (/^[A-Za-z][A-Za-z0-9+.-]*:\/\//.test(url) || /^www\./i.test(url)) {
    return true;
  }

  const host = url.split(/[/?#:]/)[0];
  const tld = host.slice(host.lastIndexOf(".") + 1);

  return tld === tld.toLowerCase() || tld === tld.toUpperCase();
}

/**
 * Finds every link in a string.
 * @param {string} text
 * @returns {Array<{ text: string, href: string, start: number, end: number }>}
 */
export function findLinks(text) {
  if (!text || typeof text !== "string") return [];

  const links = [];
  const regex = new RegExp(LINK_REGEX.source, LINK_REGEX.flags);
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match[0].length === 0) {
      regex.lastIndex++;
      continue;
    }
    // Group 1 is the email alternative: skip it, but keep the scan position
    // past it so its domain is not re-matched.
    if (match[1]) continue;

    const display = trimTrailingPunctuation(match[0]);
    const start = match.index;

    if (!isPlausibleBareDomain(display)) {
      regex.lastIndex = start + display.length;
      continue;
    }

    links.push({
      text: display,
      href: toHref(display),
      start,
      end: start + display.length,
    });

    regex.lastIndex = start + display.length;
  }

  return links;
}

/**
 * Returns the absolute URLs found in a string, de-duplicated. Used for link
 * previews.
 * @param {string} text
 * @returns {string[]}
 */
export function extractUrls(text) {
  const seen = new Set();

  return findLinks(text).reduce((urls, link) => {
    if (!seen.has(link.href)) {
      seen.add(link.href);
      urls.push(link.href);
    }
    return urls;
  }, []);
}

/**
 * Turns a plain message string into React nodes with clickable links.
 * @param {string} text
 * @returns {React.ReactNode}
 */
export function renderTextWithLinks(text) {
  if (!text || typeof text !== "string") return text;

  const links = findLinks(text);
  if (links.length === 0) return text;

  const nodes = [];
  let cursor = 0;

  links.forEach((link, index) => {
    if (link.start > cursor) {
      nodes.push(text.slice(cursor, link.start));
    }

    nodes.push(
      <a
        key={`link-${index}-${link.start}`}
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className="msg-link"
        onClick={(e) => e.stopPropagation()}
      >
        {link.text}
      </a>
    );

    cursor = link.end;
  });

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
}
